<template>
  <div
    :class="controlClasses"
    class="mds-form-control mds-input-number"
    @mouseenter="showDisabledHint($event)"
    @mouseleave="hideDisabledHint()"
  >
    <label :for="uid" :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :style="innerStyles" class="inner">
      <!-- Верх: кнопки, иконка, input, unit, очистка -->
      <div class="control-row">
        <button
          @click="decrease"
          class="control-btn decrease"
          type="button"
          v-if="showButtons"
        >
          <VIcon material="remove" />
        </button>

        <div class="mds-form-control-icon" v-if="hasIconSlot">
          <!-- @slot Слот иконки -->
          <slot name="icon" />
        </div>

        <input
          :disabled="disabled"
          :id="uid"
          :ref="uid"
          :placeholder="placeholder"
          :value="localValue"
          type="text"
          @input="onInput"
          @blur="onBlur"
          @focus="onFocus"
        />

        <span class="unit" v-if="unitLabel !== null">{{ unitLabel }}</span>

        <button @click="onClear" class="clear" type="button" v-if="showClear">
          <VIcon material="cancel" />
        </button>

        <button
          @click="increase"
          class="control-btn increase"
          type="button"
          v-if="showButtons"
        >
          <VIcon material="add" />
        </button>
      </div>

      <!-- Низ: сообщения -->
      <div class="messages-row" v-if="isErrorsVisible">
        <div class="error" v-if="validation.isValid === false">
          <div
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </div>
        </div>

        <div class="warning" v-if="validation.warnings.length > 0">
          <div
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
            class="warning-message"
          >
            {{ warning }}
          </div>
        </div>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./VInputNumberRusV2.scss" />

<script lang="ts">
import Unit         from '@/domains/Units/Unit';
import Core         from "@/core/Core";
import VBaseControl from "@/core/services/VBaseControl";
import Validation   from "@/core/services/Validation";
import Numeric      from "@/core/types/Numeric";
import Decimal      from 'decimal.js';

/**
 * Конструктор семантического числового типа (Qty/Price/Sum/CurrencyRate).
 * Передаётся пропом `type` и служит ЕДИНСТВЕННЫМ источником:
 *  - класса для создания значения из ввода (type.fromInput(str));
 *  - точности через статический SCALE (precision поля).
 */
type NumericClass = (new (value: Decimal.Value) => Numeric) & {
  SCALE: number;
  fromServer(value: string): Numeric;
  fromInput(input: string | null): Numeric;
  fromEmpty(): Numeric;
  zero(): Numeric;
};

/**
 * VInputNumberRusV2 — числовой ввод, работающий НАТИВНО с семантическими
 * типами (Numeric: Qty/Price/Sum/CurrencyRate). Снаружи:  v-model="position.qty"
 * :type="Qty"  — никакой обвязки.
 *
 * Отличия от V1 (примитивного):
 *  - v-model принимает и отдаёт Numeric|null (а не number|string);
 *  - точность (precision) берётся из type.SCALE, проп precision удалён;
 *  - проп `type` (класс) обязателен — источник типа и scale, работает и при null;
 *  - localValue (строка в input) по-прежнему сырая, эмит — на каждый ввод
 *    (каскадные сеттеры модели пересчитываются сразу, это требование UX).
 *
 * Нормализация ввода (_formatNumberString) перенесена из V1 без изменений —
 * она работает со строкой поля и от типа значения не зависит.
 */
export default defineComponent({
  name: 'VInputNumberRusV2',

  extends: VBaseControl,

  props: {
    /**
     * Класс семантического типа значения (Qty, Price, Sum, CurrencyRate).
     * Обязателен: источник типа для создания значения и scale, поскольку мы не можем опирать на тип значения в v-model,
     * он может быть и будет Null
     */
    type:        { type: Function as PropType<NumericClass>, required: true },
    /** Значение. Numeric или null (пустое поле — штатно). */
    modelValue:  { type: Object as PropType<Numeric | null>, default: null },
    /** Показывать кнопку очистки. */
    clearable:   { type: Boolean,          default: false },
    /** Валидация диапазона по строке формата "10.3" (min/max разрядность). */
    range:       { type: [Number, String], default: null },
    /** Шаг изменения по кнопкам. Обычно целое, бывает дробное (объём фасовки). */
    step:        { type: Number,           default: 1 },
    /** Показывать кнопки увеличения/уменьшения. */
    showButtons: { type: Boolean,          default: false },
    /** Текстовая подсказка справа от контрола. */
    helperText:  { type: String, default: null, validator: (value: string) => typeof value === 'string' && value.length > 0 },
    /** Единица измерения справа. Строка ("кг", "$") или объект Unit. */
    unit:        { type: [String, Object] as PropType<string | Unit>, default: null },
  },

  emits: ['update:modelValue', 'input', 'blur', 'focus'],

  data() {
    const validation = Validation.fromProps(this as any);

    const rules = this.parseRangeRules();
    if (rules.min !== null) validation.min(rules.min);
    if (rules.max !== null) validation.max(rules.max);

    return {
      validation,
      localValue:         null as string | null,
      // Кэш внешнего значения для разведения «строки в поле» и «значения модели».
      // Храним строковое представление (toJSON), чтобы сравнивать дёшево.
      externalValueCache: null as string | null,
    };
  },

  computed: {
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.clearable === true)   classes.push('clearable');
      if (this.showButtons === true) classes.push('show-buttons');
      return classes;
    },

    showClear(): boolean {
      return this.clearable === true
        && this.localValue !== null
        && this.localValue.length > 0
        && this.disabled === false;
    },

    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) {
        result['width'] = `calc(100% - ${this.labelWidth})`;
      }
      return result;
    },

    hasIconSlot(): boolean {
      return this.$slots['icon'] !== undefined;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots['helper-text'] !== undefined;
    },

    /** Единица измерения — строка из пропа unit (строка или Unit.name). */
    unitLabel(): string | null {
      if (this.unit === null) {
        return null;
      } else if (this.unit instanceof Unit) {
        return this.unit.name ?? null;
      } else {
        return this.unit;
      }
    },

    /** Точность поля — из SCALE переданного типа (единственный источник). */
    getPrecision(): number {
      return this.type.SCALE;
    },

    getValidateRules() {
      return this.parseRangeRules();
    },
  },

  watch: {
    modelValue(newValue, oldValue) {
      if (newValue === oldValue) return;
      this._syncLocalValue();
    },
  },

  created() {
    this._validateModelType();
    this._syncLocalValue(true);
  },

  methods: {
    /**
     * Парсит проп range='10.3' в { min, max }. precision из range ИГНОРИРУЕТСЯ —
     * точность задаёт тип (type.SCALE), не range. Из range берём только
     * разрядность целой части (min/max диапазона).
     *
     * Границы по умолчанию (когда ни range, ни min/max не заданы):
     *  - нижняя — 0: отрицательное запрещено. Кейсов ввода отрицательных
     *    количеств/цен/сумм в проекте нет; появятся — задать явный min/range.
     *  - верхняя — 15 знаков целой части: «нельзя ввести число шире инпута»,
     *    с запасом под крупные суммы (напр. UZS). Поле сужает через range.
     */
    parseRangeRules() {
      let { min, max, range } = this;
      const DEFAULT_MIN               = 0;
      const DEFAULT_INTEGER_MAX_DIGITS = 15;
      const result: { min: number | null, max: number | null, precision: number } = {
        min:       DEFAULT_MIN,
        max:       10 ** DEFAULT_INTEGER_MAX_DIGITS - 1,
        precision: this.type.SCALE,
      };
      let rangeMax;
      let haveMinus = false;

      if (this.$core.utils.number.isNumber(range)) {
        let rangeStr = range.toString();
        if (rangeStr[0] === '-') {
          haveMinus = true;
          rangeStr = rangeStr.slice(1);
        }
        const [rangeMaxNumber, rangePrecisionNumber] = rangeStr
          .split('.')
          .map((item) => parseInt(item, 10));
        if (isNaN(rangeMaxNumber) === false && isNaN(rangePrecisionNumber) === false && rangeMaxNumber - rangePrecisionNumber >= 0) {
          rangeMax = rangeMaxNumber - rangePrecisionNumber;
        }
      }
      if (typeof range === 'string') {
        let rangeStr = range;
        if (rangeStr[0] === '-') {
          haveMinus = true;
          rangeStr = rangeStr.slice(1);
        }
        let [rangeMaxString] = rangeStr.split('.');
        if (typeof rangeMaxString === 'string' && rangeMaxString.length > 0) {
          rangeMax = rangeMaxString.length;
        }
      }
      if (this.$core.utils.number.isNumber(rangeMax)) {
        result.max = 10 ** rangeMax - 1;
        result.min = haveMinus ? (10 ** rangeMax - 1) * -1 : 0;
      }
      if (this.$core.utils.number.isNumber(min)) result.min = min;
      if (this.$core.utils.number.isNumber(max)) result.max = max;
      return result;
    },

    /**
     * Проверяет, что modelValue (если не null) — экземпляр переданного типа.
     * Рассинхрон класса — нештатная ситуация, орём через Core.error.
     */
    _validateModelType() {
      if (this.modelValue !== null && this.modelValue instanceof this.type === false) {
        Core.error(
          `VInputNumberRusV2: modelValue не является экземпляром переданного type (${this.type.name})`,
        );
      }
    },

    onInput(event) {
      this.localValue = this._formatNumberString(event.target.value);
      event.target.value = this.localValue;

      const value = this._buildValue(this.localValue);

      if (this.validation.isDirty === false) {
        const prev = this.externalValueCache;
        const next = value.isEmpty() ? null : value.toJSON();
        if (prev !== next) {
          this.validation.markDirty();
        }
      }

      this.externalValueCache = value.isEmpty() ? null : value.toJSON();
      this._emitValue(value);
    },

    async onBlur(event) {
      if (typeof this.debounceValidation.cancel === 'function') {
        this.debounceValidation.cancel();
      }

      await this.validate();

      const value = this._buildValue(event.target.value);
      this.localValue = value.isEmpty() ? null : this._formatValueForInput(value);
      this.externalValueCache = value.isEmpty() ? null : value.toJSON();
      event.target.value = this.localValue ?? '';
      this.$emit('blur', value);
    },

    onFocus(event) {
      this.validation.markTouched();
      this.$emit('focus', event.target);
    },

    isFocused() {
      return this.$refs[this.uid] === document.activeElement;
    },

    _emitValue(value: Numeric) {
      this.$emit('update:modelValue', value);
      this.$emit('input', value);
    },

    /**
     * Синхронизирует строку в поле (localValue) с внешним значением (modelValue).
     * Разводит «то, что печатает пользователь» и «значение модели», чтобы
     * внешнее присваивание не дёргало ввод под курсором.
     */
    _syncLocalValue(force = false) {
      const external = this.modelValue;
      const externalKey = external !== null ? external.toJSON() : null;

      if (force === false && externalKey === this.externalValueCache) return;

      this.externalValueCache = externalKey;
      this.localValue = external !== null ? this._formatValueForInput(external) : null;

      if (force === false) {
        this.debounceValidation();
      }
    },

    /**
     * Строит значение своего типа из введённой строки.
     *
     * Пустое и неразобранное поле дают не null, а fromEmpty(): тип у контрола
     * известен (проп type), и наружу должно уходить значение, а не его
     * отсутствие. Иначе модель получает null в поле, объявленное Numeric, и
     * первое же обращение к методу (isEmpty, greaterThan) роняет рендер.
     * Пустота остаётся различимой — isEmpty() отвечает true.
     *
     * Разбор (запятая, разделители разрядов, незавершённое "12,") — в fromInput.
     */
    _buildValue(stringValue: string | null): Numeric {
      return this.type.fromInput(stringValue);
    },

    /**
     * Убирает разделители разрядов. Снимаются оба вида пробела: неразрывный
     * ставит группировка, обычный приезжает копипастом.
     */
    _stripGroupSeparators(value: string): string {
      return value.replace(/[\s\u00A0]/g, '');
    },

    /**
     * Строка поля из готового значения: хвостовые нули убраны, разряды
     * разделены.
     *
     * Отдельно от _formatNumberString, потому что обрезка нулей допустима
     * только здесь — значение пришло извне и уже завершено. Делать её во
     * время набора нельзя: "5.10" схлопнулось бы в "5.1" под пальцами, и
     * дописать вторую цифру стало бы некуда.
     */
    _formatValueForInput(value: Numeric): string {
      /** Число строкой с точкой ("5.100"); у пустого значения toJSON отдаёт null */
      const rawNumber = value.toJSON() ?? '';

      /** Без хвостовых нулей: "5.100" → "5.1" */
      const trimmedNumber = this._trimTrailingZeros(rawNumber);

      /** С разделителями разрядов и запятой: "1234.5" → "1 234,5"; на пустой строке — null */
      const formattedNumber = this._formatNumberString(trimmedNumber);

      return formattedNumber ?? '';
    },

    /**
     * Убирает хвостовые нули дробной части, а с ними и точку, если дробной
     * части не осталось: "5.000" → "5", "5.100" → "5.1". Нули внутри дроби
     * сохраняются — режется только хвост. Работает по toJSON (точка), запятую
     * ставит уже _formatNumberString.
     *
     * Нужно, потому что toJSON печатает ровно scale знаков: цена в две
     * копейки не должна выглядеть трёхзначной.
     */
    _trimTrailingZeros(value: string): string {
      const [integerPart, decimalPart] = value.split('.');

      if (decimalPart === undefined) {
        return integerPart;
      }

      const trimmedDecimal = decimalPart.replace(/0+$/, '');

      if (trimmedDecimal.length === 0) {
        return integerPart;
      } else {
        return `${integerPart}.${trimmedDecimal}`;
      }
    },

    onClear() {
      const value = this.type.fromEmpty();

      this.localValue = null;
      this.externalValueCache = null;
      this._emitValue(value);
      this.$emit('blur', value);
    },

    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      // В Validation.validate уходит ПОСТРОЕННОЕ значение (Numeric), а не
      // сырая строка localValue. Кастомные validateMethod типизированных полей
      // ждут Qty/Price (isEmpty/greaterThan) — строка их не имеет.
      // Пустое поле — fromEmpty(), не null.
      await this.validation.validate(this._buildValue(this.localValue));
    },

    decrease() {
      // Стёртое поле — fromEmpty (NaN): от него шаг дал бы снова NaN
      const current = this.modelValue === null || this.modelValue.isEmpty() === true ? this.type.zero() : this.modelValue;
      const stepValue = new this.type(this.step);
      let result = current.minus(stepValue) as Numeric;

      const rules = this.getValidateRules;
      if (rules.min !== null && result.lessThanOrEqualTo(rules.min) === true) {
        result = new this.type(rules.min);
      }

      this.localValue = this._formatValueForInput(result);
      this.externalValueCache = result.toJSON();
      this._emitValue(result);
    },

    increase() {
      // Стёртое поле — fromEmpty (NaN): от него шаг дал бы снова NaN
      const current = this.modelValue === null || this.modelValue.isEmpty() === true ? this.type.zero() : this.modelValue;
      const stepValue = new this.type(this.step);
      let result = current.plus(stepValue) as Numeric;

      const rules = this.getValidateRules;
      if (rules.max !== null && result.greaterThanOrEqualTo(rules.max) === true) {
        result = new this.type(rules.max);
      }

      this.localValue = this._formatValueForInput(result);
      this.externalValueCache = result.toJSON();
      this._emitValue(result);
    },

    /**
     * Нормализует введённую пользователем строку в валидный числовой ввод.
     * Перенесено из V1 — работает со строкой поля, от типа значения не зависит.
     * Точность берётся из getPrecision (= type.SCALE).
     *
     * Правила: снять разделители разрядов; оставить цифры/точку/запятую;
     * запятую → точку (внутри разбор идёт по точке); оставить только первый
     * разделитель; поправить ведущие случаи (".5"→"0.5", "007"→"7"); обрезать
     * дробь до precision; обрезать целую по разрядности max; расставить
     * разделители разрядов, дробную часть отделить запятой; сохранить минус.
     */
    _formatNumberString(value) {
      let _value = null;

      if (typeof value === 'string' && value.length > 0) {
        let minus = '';
        if (value[0] === '-') {
          minus = '-';
          _value = value.slice(1);
        } else {
          _value = value;
        }

        /** Разделители \s и \u00A0 снимаем до чистки: группировка ставит их заново
         *  в конце, а до тех пор строка должна быть голым числом. */
        _value = this._stripGroupSeparators(_value);

        _value = _value.replace(/[^\d,.]+/g, '');

        if (/^[\d,.]+$/.test(_value)) {
          _value = _value.replace(/,/g, '.');

          // Дробный разделитель — первый; остальные (вставка "1.2.3") выбрасываются
          const firstDotIndex = _value.indexOf('.');
          if (firstDotIndex !== -1) {
            _value = _value.slice(0, firstDotIndex + 1) + _value.slice(firstDotIndex + 1).replace(/\./g, '');
          }

          if (_value[0] === '.') {
            _value = `0${_value}`;
          }
          else {
            // Ведущие нули целой части: "007" → "7", "0.5" не трогаем
            _value = _value.replace(/^0+(?=\d)/, '');
          }

          if (this.getPrecision === 0) {
            const _valueArray = _value.split('.');
            if (_valueArray.length > 1) {
              _value = _valueArray[0];
            }
          } else {
            const _valueArray = _value.split('.');
            if (_valueArray.length > 1) {
              _value = `${_valueArray[0]}.`;
              if (_valueArray[1].length > this.getPrecision) {
                _value += _valueArray[1].slice(0, this.getPrecision);
              } else {
                _value += _valueArray[1];
              }
            }
          }
        }

        // Потолок разрядности целой части — из вычисленных правил (parseRangeRules),
        // а не из сырого пропа this.max: max задаётся дефолтом (15 знаков) либо
        // сужается через range/max, и обрезка ввода должна следовать за ним.
        const maxRule = this.getValidateRules.max;
        if (maxRule !== null) {
          const maxIntegerLength = Math.floor(Math.abs(maxRule)).toString().length;
          const [intPart, decPart] = _value.split('.');
          if (intPart.length > maxIntegerLength) {
            _value = decPart !== undefined
              ? intPart.slice(0, maxIntegerLength) + '.' + decPart
              : intPart.slice(0, maxIntegerLength);
          }
        }

        _value = this._addGroupSeparators(_value);

        _value = minus + _value;
      }

      return _value;
    },

    /**
     * Расставляет разделители разрядов в целой части. Группировка — общая
     * StringUtils.formatWithDelimiter, та же, что делит номера: справа налево
     * по три символа. Разделитель неразрывный, чтобы число не рвалось по
     * строкам.
     *
     * На вход строка с точкой (внутренний вид), на выход — с запятой (вид поля).
     * Дробная часть не группируется, поэтому отделяется до вызова и
     * приклеивается обратно. Незавершённый ввод "12," сохраняется как есть —
     * иначе запятая исчезала бы прямо под пальцами.
     */
    _addGroupSeparators(value: string): string {
      const [integerPart, decimalPart] = value.split('.');

      const formattedInteger = this.$core.utils.string.formatWithDelimiter(integerPart, 3, '\u00A0');

      if (decimalPart === undefined) {
        return formattedInteger;
      }
      else if (decimalPart.length === 0) {
        // Пользователь только что поставил разделитель — оставляем его на месте
        return `${formattedInteger},`;
      }
      else {
        return `${formattedInteger},${decimalPart}`;
      }
    },
  },
});
</script>